import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { InstallAppButton } from "../components/InstallAppButton";
import { api, Campaign, CallLog } from "../lib/api";

export function DashboardPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, logs] = await Promise.all([api.listCampaigns(), api.listCalls()]);
        if (!alive) return;
        setCampaigns(c.campaigns);
        setCalls(logs.calls.slice(0, 12));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to load dashboard");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const running = campaigns.filter((c) => c.status === "running").length;
  const answered = calls.filter((c) => c.status === "answered" || c.status === "completed").length;
  const maxCh = Math.max(0, ...campaigns.map((c) => c.maxConcurrent || 0), 120);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Dialer dashboard</h1>
          <p>
            Launch high-concurrency AI voice campaigns. Designed for <strong>100+</strong> simultaneous
            outbound channels (carrier account limits apply).
          </p>
        </div>
        <div className="row-actions">
          <Link className="btn success" to="/dialer">
            Open dialer
          </Link>
          <Link className="btn" to="/campaigns">
            Campaigns
          </Link>
          <Link className="btn ghost" to="/settings">
            Provider settings
          </Link>
          <InstallAppButton />
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="grid-stats">
        <div className="stat-card">
          <div className="label">Campaigns</div>
          <div className="value">{campaigns.length}</div>
          <div className="hint">{running} running</div>
        </div>
        <div className="stat-card">
          <div className="label">Peak concurrency setting</div>
          <div className="value">{maxCh}</div>
          <div className="hint">per campaign channels</div>
        </div>
        <div className="stat-card">
          <div className="label">Recent answers</div>
          <div className="value">{answered}</div>
          <div className="hint">from latest call log window</div>
        </div>
        <div className="stat-card">
          <div className="label">Subscription</div>
          <div className="value" style={{ fontSize: "1.15rem" }}>Pro</div>
          <div className="hint">$99/wk · $297/mo · $2,970/yr</div>
        </div>
      </div>

      <div className="card">
        <div className="section-title">
          <h2>Quick start</h2>
        </div>
        <ol className="muted" style={{ lineHeight: 1.7, margin: 0, paddingLeft: "1.2rem" }}>
          <li>
            Open <Link to="/settings">Settings</Link> and add Twilio, Telnyx, and/or SIP credentials.
          </li>
          <li>Optionally add your ElevenLabs API key for premium AI voices.</li>
          <li>
            Import <Link to="/contacts">contacts</Link>, create a campaign script, set concurrency ≥ 100.
          </li>
          <li>
            Open the <Link to="/dialer">Dialer</Link>: use the dialpad for a single call, or load a
            campaign and press <strong>Start dialing</strong>.
          </li>
          <li>
            On answer, the dialer delivers your TTS / ElevenLabs message automatically via webhooks.
          </li>
        </ol>
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <h2>Recent calls</h2>
        {calls.length === 0 ? (
          <p className="muted">No calls yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Phone</th>
                <th>Status</th>
                <th>Provider</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.phone}</td>
                  <td>
                    <span className={`badge ${c.status}`}>{c.status}</span>
                  </td>
                  <td>{c.provider}</td>
                  <td className="muted">{new Date(c.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
